import React, { useState, useEffect } from "react";
import { Button, Modal, Input, Form, Upload, Skeleton, message } from "antd";
import { EditOutlined, UploadOutlined } from "@ant-design/icons";

// Import the service needed to update the bulletin
import { useBulletinService } from "../../services";

import "../../styles/bulletinAdmin/components/bulletinCarousel.scss";

export default function EBulletinCarousel({
  bulletin,
  isAdmin,
  reloadBulletin,
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editData, setEditData] = useState({ title: "", image: "" });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { service: bulletinService } = useBulletinService();

  // When the bulletin data changes, update the loaded state
  useEffect(() => {
    setIsLoaded(false);
  }, [bulletin?.cover_image]);

  const showModal = () => {
    // Populate the modal with the current bulletin's data
    setEditData({
      title: bulletin.title,
      description: bulletin.description,
      image: bulletin.cover_image,
    });
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    setIsSaving(true);
    try {
      // Call the service to update the bulletin with the new data
      await bulletinService.updateBulletin(bulletin.id, {
        title: editData.title,
        description: editData.description,
        cover_image: editData.image,
      });
      // Trigger the reload function passed from the parent to refetch data
      if (reloadBulletin) {
        await reloadBulletin();
      }
      message.success("Cover page updated successfully!");
      setIsModalVisible(false);
    } catch (error) {
      console.error("Failed to update cover page:", error);
      message.error("Failed to update cover page.");
    } finally {
      setIsSaving(false);
    }
  };

  // If there's no bulletin data, show a placeholder
  if (!bulletin) {
    return (
      <div className="cover-page-container">
        <Skeleton.Node
          active={true}
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="cover-page-container">
        {/* Skeleton loader while the image is loading */}
        {!isLoaded && (
          <Skeleton.Node
            active={true}
            style={{ width: "100%", height: "100%" }}
          />
        )}

        <img
          src={bulletin.cover_image}
          alt={bulletin.title}
          className={isLoaded ? "visible" : "hidden"}
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)} // Also mark as loaded on error to hide skeleton
        />

        {/* Overlay and text are always present but rely on the image being loaded via CSS */}
        <div className="overlay" />
        <div className="text-overlay">
          <h2>{bulletin.title}</h2>
          <h3>{bulletin.description}</h3>
        </div>

        {/* Show the edit button only if the user is an admin */}
        {isAdmin && (
          <div className="edit-button">
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={showModal}
            />
          </div>
        )}
      </div>

      <Modal
        title="Edit Cover Page"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={isSaving}
        okText={isSaving ? "Saving..." : "Save"}
        okButtonProps={{
          style: { backgroundColor: "#8D1437", color: "white" },
        }}
        cancelButtonProps={{
          style: { borderColor: "#8D1437", color: "#8D1437" },
        }}
      >
        <Form layout="vertical">
          <Form.Item label="Title">
            <Input
              value={editData.title}
              onChange={(e) =>
                setEditData({ ...editData, title: e.target.value })
              }
            />
          </Form.Item>
          <Form.Item label="Description">
            <Input.TextArea
              value={editData.description}
              onChange={(e) =>
                setEditData({ ...editData, description: e.target.value })
              }
              rows={4}
            />
          </Form.Item>
          <Form.Item label="Image URL">
            <div className="flex gap-2">
              <Input
                value={editData.image}
                onChange={(e) =>
                  setEditData({ ...editData, image: e.target.value })
                }
              />
              <Upload
                showUploadList={false}
                beforeUpload={(file) => {
                  const reader = new FileReader();
                  reader.onload = (e) =>
                    setEditData({ ...editData, image: e.target.result });
                  reader.readAsDataURL(file);
                  return false; // Prevent automatic upload
                }}
              >
                {/* <Button icon={<UploadOutlined />}>Upload</Button> */}
              </Upload>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
